import binance from '../services/binance_service';
import coinbase from '../services/coinbase_service';
import kraken from '../services/kraken_service';
import bybit from '../services/bybit_service';
import okx from '../services/okx_service';
import { fetchEarlierHistory, supportsHistory } from '../services/chart-history';
import { binanceInterval, binanceSubscriptions, klineCandle, mergeRecoveredCandles, recoverCandles } from '../services/live-candles.mjs';
const publicFeeds = { Coinbase:coinbase, Kraken:kraken, Bybit:bybit, OKX:okx };
export default {
    data: () => ({ feedStatus:'connecting', feedLastTick:0, feedVolumeSync:0, feedClock:Date.now(), feedRecovering:false, feedError:'' }),
    computed: {
        feedVolumeLabel() { return this.connector==='AMFI'?'Published NAV':this.connector==='Binance'?'Exchange candle volume':supportsHistory(this.connector)?'Volume · history sync':'Historical volume only'; },
        feedLabel() {
            if(this.connector==='AMFI')return 'Historical NAV';
            if (this.workspaceReplay?.active) return 'Replay · historical data';
            if (this.feedRecovering && !this.feedLastTick) return 'Recovering candles';
            if (this.feedError) return 'Recovery needed';
            if (this.feedStatus === 'reconnecting') return 'Reconnecting';
            if (!this.feedLastTick) return this.feedStatus === 'connected' ? 'Connected · awaiting prices' : 'Awaiting feed';
            return this.feedClock-this.feedLastTick > 30000 ? 'No recent prices' : 'Live prices';
        },
        feedDescription() {
            const age = this.feedLastTick ? Math.floor((this.feedClock-this.feedLastTick)/1000)+'s ago' : 'awaiting first price';
            const volume = supportsHistory(this.connector) && this.connector!=='Binance' && this.connector!=='AMFI' ? 'volume refreshed from history every 15s; open-candle volume can lag' : this.feedVolumeLabel;
            return `${this.connector} · last price ${age} · ${volume}${this.feedVolumeSync ? ' · volume synced '+new Date(this.feedVolumeSync).toLocaleTimeString() : ''}${this.feedError ? ' · '+this.feedError : ''}`;
        },
    },
    methods: {
        feedSocket() { return this.connector === 'Binance' ? (/\.P$/i.test(this.symbol) ? binance.binance_futures_connection : binance.binance_spot_connection) : publicFeeds[this.connector]?.connection; },
        feedReceived() { this.feedLastTick = Date.now(); this.feedStatus='connected'; },
        subscribeBinanceCandles() {
            const params = binanceSubscriptions(this.symbol || this.query, Object.keys(this.chart));
            if (!params.length) return;
            const message=JSON.stringify({method:'SUBSCRIBE',params,id:2});
            if (/\.P$/i.test(this.symbol || this.query)) binance.sendFuturesWs(message); else binance.sendSpotWs(message);
        },
        applyBinanceCandle(event) {
            if (this.protectChart) return;
            const data=event.data || event, k=data.k;
            const pair=String(this.symbol || this.query).split(':').pop().replace('/','').replace(/\.P$/i,'').toUpperCase();
            if (!k || k.s !== pair) return;
            const candle=klineCandle(k); if (!candle) return;
            const streamKey=pair+':'+k.i;
            this._feedKlineTimes ||= {};
            const emitted=Number(data.E);
            if(Number.isFinite(emitted) && emitted<(this._feedKlineTimes[streamKey] || 0))return;
            if(Number.isFinite(emitted))this._feedKlineTimes[streamKey]=emitted;
            this.feedReceived(); this.tick_count++;
            for (const tf of Object.keys(this.chart)) {
                if (binanceInterval(tf) !== k.i) continue;
                const cube=this.chart[tf], rows=cube?.data?.chart?.data;
                if (!rows?.length || candle[0]<rows[rows.length-1][0]) continue;
                if (this._feedRecovery) (this._feedRecovery.live[tf] ||= new Map()).set(candle[0],candle);
                cube.update({candle, t:candle[0]});
            }
            for(const tf of Object.keys(this.chart)) {
                const custom=this.getCustomTimeframe(tf);
                if(!custom)continue;
                const base=this.getBestLoadedBaseTimeframe(custom);
                if(binanceInterval(base)!==k.i)continue;
                const rows=this.deriveCustomTimeframeData(custom);
                this.$set(this.chart[tf].data.chart,'data',rows);
                if(this.dataset[tf]?.chart)this.$set(this.dataset[tf].chart,'data',rows);
            }
            this.feedVolumeSync=Date.now();
        },
        feedTransport(state, error) {
            this.feedStatus=state;
            if(error)this.feedError=error;
            if(state==='connected')this.recoverFeed();
        },
        async recoverFeed(periodic = false) {
            if (this.feedRecovering || this.protectChart || !supportsHistory(this.connector)) return;
            const key=this.workspaceKey, generation=this._loadGeneration;
            const job={live:{}}; this._feedRecovery=job; this.feedRecovering=true; this.feedError='';
            const context={connector:this.connector,symbol:this.symbol,pairId:this.pairId,exchange:this.exchange};
            const view=this.$refs.tv;
            let range;
            try {
                const plans=[];
                const visible = new Set([this.timeframe, ...(this.workspaceSecondaryTf || []).slice(0,Math.max(0,this.workspaceLayout-1))]);
                for(const tf of [...visible]) { const custom=this.getCustomTimeframe(tf);if(custom)visible.add(this.getBestLoadedBaseTimeframe(custom)); }
                for (const tf of Object.keys(this.chart)) {
                    if(periodic && !visible.has(tf))continue;
                    if (this.getCustomTimeframe(tf)) continue;
                    const cube=this.chart[tf], rows=cube?.data?.chart?.data;
                    if (!rows?.length) continue;
                    const anchor=this._feedAnchors?.[tf] ?? rows[rows.length-1][0];
                    const recovered=await recoverCandles(cursor=>this.feedHistoryPage({...context,timeframe:tf},cursor),anchor,Date.now());
                    if (key!==this.workspaceKey || generation!==this._loadGeneration || cube!==this.chart[tf]) return;
                    plans.push({tf,cube,recovered});
                }
                if(!plans.length)return;
                if(plans.some(({tf,cube})=>this.chart[tf]!==cube))return;
                // Capture at commit time: a user may pan while recovery awaits the network.
                range=view===this.$refs.tv ? view?.getRange()?.slice() : null;
                // Commit only after every interval recovers successfully.
                for (const {tf,cube,recovered} of plans) {
                    const merged=mergeRecoveredCandles(cube.data.chart.data,recovered,[...(job.live[tf]?.values() || [])]);
                    // AggTool retains its last OHLCV update between timer flushes.
                    // Drop it so a pre-recovery snapshot cannot overwrite repaired bars.
                    if(cube.agg?.symbols)delete cube.agg.symbols.ohlcv;
                    this.$set(cube.data.chart,'data',merged);
                    this._feedAnchors ||= {};
                    this._feedAnchors[tf]=merged[merged.length-1][0];
                    if(this.dataset[tf]?.chart)this.$set(this.dataset[tf].chart,'data',merged);
                }
                for (const tf of Object.keys(this.chart)) if(this.getCustomTimeframe(tf)) {
                    const rows=this.deriveCustomTimeframeData(this.getCustomTimeframe(tf));
                    this.$set(this.chart[tf].data.chart,'data',rows);
                    if(this.dataset[tf]?.chart)this.$set(this.dataset[tf].chart,'data',rows);
                }
                this.feedVolumeSync=Date.now();
                this.refreshCurrentIndicators(this.timeframe);
                await this.$nextTick();
                if(view===this.$refs.tv && range?.length===2)view.setRange(...range);
            } catch(error) { if(key===this.workspaceKey)this.feedError=error.message; }
            finally { if(this._feedRecovery===job){this._feedRecovery=null;this.feedRecovering=false;} }
        },
        async feedHistoryPage(context,cursor) {
            let timer;
            try { return await Promise.race([
                fetchEarlierHistory(context,cursor),
                new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error('History recovery timed out. Retry when the provider is available.')),12000);}),
            ]); } finally { clearTimeout(timer); }
        },
        feedPoll() {
            this.feedClock=Date.now();
            this._feedAnchors ||= {};
            for(const tf of Object.keys(this.chart)) {
                const rows=this.chart[tf]?.data?.chart?.data;
                if(rows?.length && this._feedAnchors[tf]===undefined)this._feedAnchors[tf]=rows[rows.length-1][0];
            }
            const socket=this.feedSocket(), state=socket?.readyState;
            if (state===1 && this._feedSocketState!==1) {
                this.feedStatus='connected';
                // Initial handshakes also reconcile the REST-to-stream gap.
                this.recoverFeed();
            } else if(state===2 || state===3 || state===0 && this.feedLastTick) this.feedStatus='reconnecting';
            this._feedSocketState=state;
            if(this.connector!=='Binance' && this.connector!=='AMFI' && supportsHistory(this.connector) && this.feedClock-(this._feedRefreshAt || 0)>15000) {
                this._feedRefreshAt=this.feedClock; this.recoverFeed(true);
            }
        },
    },
    watch: {
        workspaceKey() { this._feedAnchors={};this._feedKlineTimes={};this.feedLastTick=0;this.feedVolumeSync=0;this.feedStatus='connecting';this.feedError='';this._feedSocketState=undefined; },
    },
    mounted() { this._feedTimer=setInterval(()=>this.feedPoll(),1000); },
    beforeDestroy() { clearInterval(this._feedTimer);this._loadGeneration++; },
};
